import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import type { OfferingV2Response } from '../types/offeringsV2';
import type { OfferResponse, OfferStatus } from '../types/offers';
import { useOffersT } from '../components/offers/offersI18n';
import {
  EDITABLE_STATUSES, MAX_BOOKINGS_MAX, MAX_WINDOW_DAYS, PERCENT_MAX, PERCENT_MIN, FIXED_MIN, RUNNING_STATUSES, SUBTITLE_MAX, TITLE_MAX,
  addDays, applyDefaultToAll, emptyForm, fieldStep, firstErrorStep, formatInstantDate, offeringFromPrice, offeringHasPriceRange, formFromOffer, formToRequest, formatAed, formatDate,
  itemField, itemValues, itemValuesDiffer, mapOfferError, maxItemValue, minItemValue,
  previewDiscountedPrice, sortOfferItems, statusChipClass, todayInDubai, toggleItem, validateAll, validateStep,
  type OfferField, type OfferFieldErrors, type OfferForm, type ValidationContext,
} from '../components/offers/offerUtils';
import BannerUploader from '../components/offers/BannerUploader';
import OfferPhonePreview from '../components/offers/OfferPhonePreview';
import ConfirmDialog from '../components/offers/ConfirmDialog';
import OfferVariantPrices from '../components/offers/OfferVariantPrices';
import { useOfferVariantPreview } from '../components/offers/useOfferVariantPreview';

const STEP_KEYS = ['offers.step.details', 'offers.step.services', 'offers.step.banner', 'offers.step.preview'];
const LAST_STEP = STEP_KEYS.length - 1;
const offerStepStorageKey = (offerId: string) => `garagat_offer_step_${offerId}`;

function readStoredStep(offerId: string): number | null {
  try {
    const raw = localStorage.getItem(offerStepStorageKey(offerId));
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 && n <= LAST_STEP ? n : null;
  } catch {
    return null;
  }
}

function storeStep(offerId: string, step: number) {
  try { localStorage.setItem(offerStepStorageKey(offerId), String(step)); } catch { /* private mode */ }
}

interface ServiceOption {
  id: string;
  name: string;
  /** Lowest list price ("from"); null when the offering has no price yet. */
  price: number | null;
  /** The active variants span more than one price, so the list shows "from". */
  ranged?: boolean;
  inactive?: boolean;
}

function toOption(o: OfferingV2Response): ServiceOption {
  return { id: o.id, name: o.name, price: offeringFromPrice(o), ranged: offeringHasPriceRange(o) };
}

type Busy = null | 'step' | 'save' | 'submit' | 'withdraw';

function OfferEditorPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, lang } = useOffersT();
  const today = useMemo(() => todayInDubai(), []);
  const isNewRoute = !routeId || routeId === 'new';

  const [offer, setOffer] = useState<OfferResponse | null>(null);
  const [form, setForm] = useState<OfferForm>(() => emptyForm(today));
  const [dirty, setDirty] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(!isNewRoute);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<OfferFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [services, setServices] = useState<ServiceOption[] | null>(null);
  const [servicesError, setServicesError] = useState(false);
  const [garageName, setGarageName] = useState('');
  const [previewLang, setPreviewLang] = useState<'en' | 'ar'>(lang);
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');

  // ── Load the offer (edit), the garage's active services and the garage name ──
  const applyOffer = useCallback((o: OfferResponse, keepLocalBanners?: OfferForm) => {
    setOffer(o);
    const next = formFromOffer(o.pendingRevision ?? o);
    // A fresh upload has a local preview URL; keep it when the server sends no download URL.
    if (keepLocalBanners) {
      if (!next.bannerUrl && next.bannerStorageKey === keepLocalBanners.bannerStorageKey) next.bannerUrl = keepLocalBanners.bannerUrl;
      if (!next.bannerArUrl && next.bannerArStorageKey === keepLocalBanners.bannerArStorageKey) next.bannerArUrl = keepLocalBanners.bannerArUrl;
    }
    setForm(next);
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!token || isNewRoute || !routeId) return;
    if (offer?.id === routeId) return; // just created here; state is already current
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api.getMyOffer(routeId, token)
      .then((o) => {
        if (cancelled) return;
        applyOffer(o);
        const effective = o.pendingRevision ?? o;
        const readOnlyStart = o.status === 'ENDED' || effective.status === 'PENDING_APPROVAL';
        setStep(readStoredStep(o.id) ?? (readOnlyStart ? LAST_STEP : 0));
      })
      .catch((err) => {
        if (cancelled) return;
        const m = mapOfferError(err, t);
        setLoadError(m.code === 'FEATURE_DISABLED' ? t('offers.err.featureDisabled') : t('offers.editor.loadFailed'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.listMyOfferingsV2(token)
      .then((list) => {
        if (cancelled) return;
        setServices((list ?? []).filter((o) => o.status === 'ACTIVE' && !o.blockedAt).map(toOption));
      })
      .catch(() => { if (!cancelled) setServicesError(true); });
    api.getMyGarage(token)
      .then((g) => { if (!cancelled) setGarageName(g?.name ?? ''); })
      .catch(() => { /* preview falls back to an empty name */ });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  // ── Derived state ──
  const effective = offer ? (offer.pendingRevision ?? offer) : null;
  const baseStatus: OfferStatus | null = offer?.status ?? null;
  const effStatus: OfferStatus | null = effective?.status ?? null;
  const isRunning = !!baseStatus && RUNNING_STATUSES.includes(baseStatus);
  const readOnly = !!offer && (baseStatus === 'ENDED' || !!offer.hiddenAt || effStatus === 'PENDING_APPROVAL');
  const canWithdraw = effStatus === 'PENDING_APPROVAL';
  /** New/DRAFT/REJECTED/SUSPENDED save on every step change; running offers only on explicit save. */
  const autosave = !readOnly && (!offer || (!!baseStatus && EDITABLE_STATUSES.includes(baseStatus)));
  const canSubmit = !readOnly && (
    !offer || (!!baseStatus && EDITABLE_STATUSES.includes(baseStatus)) || isRunning
  );

  // Options = active services + any covered service that is no longer active (so it can be removed).
  const options: ServiceOption[] = useMemo(() => {
    const base = services ?? [];
    const known = new Set(base.map((s) => s.id));
    const extra = (effective?.items ?? [])
      .filter((i) => !known.has(i.offeringId))
      .map((i) => ({ id: i.offeringId, name: i.name, price: i.listPrice, inactive: true }));
    return [...base, ...extra];
  }, [services, effective]);

  const priceOf = useCallback((offeringId: string): number | null => {
    const fromOptions = options.find((o) => o.id === offeringId)?.price;
    if (fromOptions != null) return fromOptions;
    return effective?.items?.find((i) => i.offeringId === offeringId)?.listPrice ?? null;
  }, [options, effective]);

  const validationCtx: ValidationContext = useMemo(() => ({
    today,
    startLocked: isRunning && !!effective && form.startsAt === (effective.startsAt ?? '').slice(0, 10),
    priceOf,
    bookingsUsed: offer?.bookingsUsed ?? 0,
  }), [today, isRunning, effective, form.startsAt, priceOf, offer]);

  const maxPerNum = form.maxDiscountPerBooking.trim() === '' ? null : Number(form.maxDiscountPerBooking);
  /** Spec 073: each checked service's own value (null while empty). */
  const rowValues = useMemo(() => {
    const values = itemValues(form);
    return new Map(form.items.map((i, idx) => [i.offeringId, values[idx]]));
  }, [form]);
  const offeringIds = useMemo(() => form.items.map((i) => i.offeringId), [form.items]);

  /** Decision "4 preview": the server prices each service's own value per bookable variant. */
  const variantPreview = useOfferVariantPreview(token, {
    discountType: form.discountType,
    maxDiscountPerBooking: maxPerNum,
    items: form.items.map((i) => ({ offeringId: i.offeringId, discountValue: rowValues.get(i.offeringId) ?? null })),
  });

  /**
   * Preview items: the server's prices and order when nothing changed, otherwise a local preview in the
   * server's order (largest saving first), so the phone card leads with what the customer will see.
   */
  const previewItems = useMemo(() => {
    if (effective && !dirty && effective.items?.length) return effective.items;
    return sortOfferItems(offeringIds.map((id) => {
      const opt = options.find((o) => o.id === id);
      const saved = effective?.items?.find((i) => i.offeringId === id);
      const list = priceOf(id) ?? 0;
      return {
        offeringId: id,
        name: opt?.name ?? saved?.name ?? '',
        listPrice: list,
        discountedPrice: previewDiscountedPrice(list, form.discountType, rowValues.get(id) ?? 0, maxPerNum),
        bookable: saved?.bookable ?? !opt?.inactive,
      };
    }));
  }, [effective, dirty, offeringIds, rowValues, form.discountType, maxPerNum, options, priceOf]);

  /**
   * Spec 073 phone badge: "Up to X% OFF" when the services' values differ, X = the server preview's
   * highest percent saving (no local percent maths); the saved offer's value or the highest typed
   * value only while that preview is not in yet.
   */
  const valuesDiffer = itemValuesDiffer(form);
  const previewMaxPercent = variantPreview.items?.length
    ? Math.max(...variantPreview.items.map((i) => i.maxPercentOff ?? 0))
    : null;
  const badgeUpTo = !valuesDiffer ? null
    : previewMaxPercent != null && previewMaxPercent > 0 ? { type: 'PERCENT' as const, value: previewMaxPercent }
      : !dirty && effective?.maxPercentOff ? { type: 'PERCENT' as const, value: effective.maxPercentOff }
        : { type: form.discountType, value: maxItemValue(form) ?? 0 };
  const badgeValue = maxItemValue(form) ?? Number(form.defaultDiscount);

  // ── Field updates (clear that field's error and the form error) ──
  const clearErrors = (fields: OfferField[]) => {
    setFieldErrors((e) => {
      const next = { ...e };
      fields.forEach((f) => { delete next[f]; });
      return next;
    });
  };

  const update = <K extends keyof OfferForm>(key: K, value: OfferForm[K], errorField?: OfferField | OfferField[]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setFormError(null);
    clearErrors(errorField ? (Array.isArray(errorField) ? errorField : [errorField]) : [key as OfferField]);
  };

  /** Spec 073: per-service edits (value, check/uncheck, Apply to all). */
  const editItems = (next: (f: OfferForm) => OfferForm, fields: OfferField[]) => {
    setForm(next);
    setDirty(true);
    setFormError(null);
    clearErrors(fields);
  };
  const allItemFields = (): OfferField[] => form.items.map((i) => itemField(i.offeringId));
  const setItemValue = (offeringId: string, value: string) => editItems(
    (f) => ({ ...f, items: f.items.map((i) => (i.offeringId === offeringId ? { ...i, discountValue: value } : i)) }),
    [itemField(offeringId), 'discountValue'],
  );
  const toggleService = (offeringId: string) => editItems((f) => toggleItem(f, offeringId), ['offeringIds', 'discountValue', itemField(offeringId)]);
  const applyToAll = () => editItems(applyDefaultToAll, ['discountValue', ...allItemFields()]);

  const showClientErrors = (errs: OfferFieldErrors) => {
    const translated: OfferFieldErrors = {};
    (Object.keys(errs) as OfferField[]).forEach((k) => { translated[k] = t(errs[k] as string); });
    setFieldErrors(translated);
    setFormError(t('offers.err.fixBelow'));
  };

  const showApiError = (err: unknown) => {
    // Per-service errors come back as items[<i>] in the order formToRequest sent them.
    const m = mapOfferError(err, t, form.items.map((i) => i.offeringId));
    setFieldErrors(m.fieldErrors);
    setFormError(m.formError ?? (Object.keys(m.fieldErrors).length ? t('offers.err.fixBelow') : null));
    const target = firstErrorStep(m.fieldErrors);
    if (target != null) setStep(target);
  };

  /** Creates the draft or PUTs the changes. Returns the fresh offer, or null on failure. */
  const persist = async (): Promise<OfferResponse | null> => {
    if (!token) return null;
    if (offer && !dirty) return offer;
    const body = formToRequest(form, offer?.version);
    try {
      const saved = offer ? await api.updateOffer(offer.id, body, token) : await api.createOffer(body, token);
      applyOffer(saved, form);
      if (!offer) navigate(`/promotions/${saved.id}`, { replace: true });
      return saved;
    } catch (err) {
      showApiError(err);
      return null;
    }
  };

  const goToStep = async (target: number) => {
    if (target === step || busy) return;
    if (readOnly) { setStep(target); if (offer) storeStep(offer.id, target); return; }
    if (target > step) {
      // Validate every step being left behind.
      let errs: OfferFieldErrors = {};
      for (let s = step; s < target; s++) errs = { ...errs, ...validateStep(s, form, validationCtx) };
      if (Object.keys(errs).length) {
        showClientErrors(errs);
        const first = firstErrorStep(errs);
        if (first != null && first < step) setStep(first);
        return;
      }
    }
    let saved: OfferResponse | null = offer;
    if (autosave && (dirty || !offer)) {
      setBusy('step');
      saved = await persist();
      setBusy(null);
      if (!saved && target > step) return;
    }
    setFormError(null);
    setStep(target);
    const offerId = saved?.id ?? offer?.id;
    if (offerId) storeStep(offerId, target);
  };

  const saveDraft = async () => {
    if (busy || readOnly) return;
    const errs = validateStep(0, form, validationCtx);
    if (Object.keys(errs).length) {
      showClientErrors(errs);
      setStep(fieldStep(Object.keys(errs)[0] as OfferField));
      return;
    }
    setBusy('save');
    const saved = await persist();
    setBusy(null);
    if (saved) {
      setToast(isRunning ? t('offers.bar.changesSaved') : t('offers.bar.saved'));
      storeStep(saved.id, step);
    }
  };

  const submit = async () => {
    if (busy || !canSubmit || !token) return;
    const errs = validateAll(form, validationCtx);
    if (Object.keys(errs).length) {
      showClientErrors(errs);
      const first = firstErrorStep(errs);
      if (first != null) setStep(first);
      return;
    }
    setBusy('submit');
    try {
      const saved = await persist();
      if (!saved) return;
      // A running offer's content PUT may already have queued the revision for approval.
      const alreadyPending = (saved.pendingRevision ?? saved).status === 'PENDING_APPROVAL';
      if (!alreadyPending) {
        try {
          await api.submitOffer(saved.id, token, saved.version);
        } catch (err) {
          showApiError(err);
          return;
        }
      }
      try {
        applyOffer(await api.getMyOffer(saved.id, token));
      } catch { /* the list shows the new state anyway */ }
      setFieldErrors({});
      setFormError(null);
      setToast(t('offers.bar.submitted'));
      setStep(LAST_STEP);
      storeStep(saved.id, LAST_STEP);
    } finally {
      setBusy(null);
    }
  };

  const withdraw = async () => {
    if (!offer || !token) return;
    setBusy('withdraw');
    try {
      await api.withdrawOffer(offer.id, token, offer.version);
      setConfirmWithdraw(false);
      try { applyOffer(await api.getMyOffer(offer.id, token)); } catch { /* keep current */ }
      setToast(t('offers.bar.withdrawn'));
    } catch (err) {
      setConfirmWithdraw(false);
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  // ── Render helpers ──
  const err = (f: OfferField) => fieldErrors[f]
    ? <div className="offer-field-error" id={`offer-err-${f}`} role="alert">{fieldErrors[f]}</div>
    : null;
  const errProps = (f: OfferField) => (fieldErrors[f]
    ? { 'aria-invalid': true as const, 'aria-describedby': `offer-err-${f}` }
    : {});

  if (loading) {
    return <div className="offer-editor"><p className="offer-muted-center" role="status">{t('offers.loading')}</p></div>;
  }
  if (loadError) {
    return (
      <div className="offer-editor">
        <button type="button" className="offer-back" onClick={() => navigate('/promotions')}>← {t('offers.editor.back')}</button>
        <div className="error-message" role="alert">{loadError}</div>
      </div>
    );
  }

  const heading = offer ? t('offers.editor.editTitle') : t('offers.editor.newTitle');
  const reasonCode = effective?.rejectionReasonCode;

  const notices = (
    <>
      {effStatus === 'REJECTED' && (
        <div className="offer-notice offer-notice--danger" data-testid="offer-rejection-banner">
          <strong>{offer?.pendingRevision ? t('offers.notice.revisionRejected') : t('offers.notice.rejected')}</strong>
          {reasonCode && <div className="offer-notice__reason">{t(`offers.reason.${reasonCode}`)}</div>}
          {effective?.rejectionReason && <div className="offer-notice__text">{effective.rejectionReason}</div>}
          <div className="offer-notice__hint">{t('offers.notice.rejectedHint')}</div>
        </div>
      )}
      {baseStatus === 'SUSPENDED' && (
        <div className="offer-notice offer-notice--danger">
          <strong>{t('offers.notice.suspended')}</strong>
          {offer?.rejectionReason && <div className="offer-notice__text">{offer.rejectionReason}</div>}
          <div className="offer-notice__hint">{t('offers.notice.suspendedHint')}</div>
        </div>
      )}
      {effStatus === 'PENDING_APPROVAL' && (
        <div className="offer-notice offer-notice--info">
          {offer?.pendingRevision ? t('offers.notice.pendingRevision') : t('offers.notice.pending')}
        </div>
      )}
      {isRunning && effStatus !== 'PENDING_APPROVAL' && (
        <div className="offer-notice offer-notice--info">
          {t('offers.notice.running', { status: t(`offers.status.${baseStatus}`) })}
        </div>
      )}
      {baseStatus === 'ENDED' && <div className="offer-notice">{t('offers.notice.ended')}</div>}
      {offer?.hiddenReason && <div className="offer-notice offer-notice--warn">{t(`offers.hidden.${offer.hiddenReason}`)}</div>}
    </>
  );

  const detailsStep = (
    <div className="offer-step-body">
      <div className="offer-grid-2">
        <div className="offer-field">
          <label htmlFor="offer-titleEn">{t('offers.field.titleEn')}</label>
          <input id="offer-titleEn" type="text" dir="ltr" maxLength={TITLE_MAX} value={form.titleEn} disabled={readOnly}
            onChange={(e) => update('titleEn', e.target.value)} {...errProps('titleEn')} />
          <div className="offer-counter">{form.titleEn.length}/{TITLE_MAX}</div>
          {err('titleEn')}
        </div>
        <div className="offer-field">
          <label htmlFor="offer-titleAr">{t('offers.field.titleAr')} <span className="offer-optional">({t('offers.field.optional')})</span></label>
          <input id="offer-titleAr" type="text" dir="rtl" lang="ar" maxLength={TITLE_MAX} value={form.titleAr} disabled={readOnly}
            onChange={(e) => update('titleAr', e.target.value)} {...errProps('titleAr')} />
          <div className="offer-counter">{form.titleAr.length}/{TITLE_MAX}</div>
          {err('titleAr')}
        </div>
        <div className="offer-field">
          <label htmlFor="offer-subtitleEn">{t('offers.field.subtitleEn')} <span className="offer-optional">({t('offers.field.optional')})</span></label>
          <input id="offer-subtitleEn" type="text" dir="ltr" maxLength={SUBTITLE_MAX} value={form.subtitleEn} disabled={readOnly}
            onChange={(e) => update('subtitleEn', e.target.value)} {...errProps('subtitleEn')} />
          <div className="offer-counter">{form.subtitleEn.length}/{SUBTITLE_MAX}</div>
          {err('subtitleEn')}
        </div>
        <div className="offer-field">
          <label htmlFor="offer-subtitleAr">{t('offers.field.subtitleAr')} <span className="offer-optional">({t('offers.field.optional')})</span></label>
          <input id="offer-subtitleAr" type="text" dir="rtl" lang="ar" maxLength={SUBTITLE_MAX} value={form.subtitleAr} disabled={readOnly}
            onChange={(e) => update('subtitleAr', e.target.value)} {...errProps('subtitleAr')} />
          <div className="offer-counter">{form.subtitleAr.length}/{SUBTITLE_MAX}</div>
          {err('subtitleAr')}
        </div>
      </div>
      <p className="offer-field-hint">{t('offers.hint.arabic')}</p>
      <div className="offer-grid-2">
        <div className="offer-field">
          <label htmlFor="offer-startsAt">{t('offers.field.startsAt')}</label>
          <input id="offer-startsAt" type="date" min={validationCtx.startLocked ? undefined : today} value={form.startsAt}
            disabled={readOnly || (validationCtx.startLocked && baseStatus !== 'SCHEDULED')}
            onChange={(e) => update('startsAt', e.target.value, ['startsAt', 'endsAt'])} {...errProps('startsAt')} />
          {err('startsAt')}
        </div>
        <div className="offer-field">
          <label htmlFor="offer-endsAt">{t('offers.field.endsAt')}</label>
          <input id="offer-endsAt" type="date" min={form.startsAt ? addDays(form.startsAt, 1) : today}
            max={form.startsAt ? addDays(form.startsAt, MAX_WINDOW_DAYS) : undefined} value={form.endsAt} disabled={readOnly}
            onChange={(e) => update('endsAt', e.target.value)} {...errProps('endsAt')} />
          {err('endsAt')}
        </div>
      </div>
      <p className="offer-field-hint">{t('offers.hint.window')}</p>
    </div>
  );

  const percent = form.discountType === 'PERCENT';
  const unit = percent ? '%' : 'AED';

  const servicesStep = (
    <div className="offer-step-body">
      <fieldset className="offer-fieldset" disabled={readOnly}>
        <legend>{t('offers.field.discountType')}</legend>
        <div className="offer-radio-row">
          <label className="partner-modal-radio-label">
            <input type="radio" name="offer-discount-type" checked={percent}
              onChange={() => update('discountType', 'PERCENT', ['discountValue', 'maxDiscountPerBooking', ...allItemFields()])} />
            {t('offers.field.percent')}
          </label>
          <label className="partner-modal-radio-label">
            <input type="radio" name="offer-discount-type" checked={!percent}
              onChange={() => update('discountType', 'FIXED', ['discountValue', 'maxDiscountPerBooking', ...allItemFields()])} />
            {t('offers.field.fixed')}
          </label>
        </div>
        <div className="offer-grid-3">
          <div className="offer-field">
            <label htmlFor="offer-defaultDiscount">
              {percent ? t('offers.field.defaultDiscountPercent') : t('offers.field.defaultDiscountFixed')}
            </label>
            <div className="offer-default-row">
              <input id="offer-defaultDiscount" data-testid="offer-default-discount" type="number" inputMode="decimal"
                min={percent ? PERCENT_MIN : FIXED_MIN} max={percent ? PERCENT_MAX : undefined} step="any"
                value={form.defaultDiscount} aria-describedby="offer-defaultDiscount-hint"
                onChange={(e) => { setForm((f) => ({ ...f, defaultDiscount: e.target.value })); setFormError(null); }} />
              <button type="button" className="offer-btn offer-btn--ghost offer-btn--small offer-apply-all" data-testid="offer-apply-all"
                title={t('offers.action.applyToAllHint')} onClick={applyToAll}
                disabled={form.items.length === 0 || form.defaultDiscount.trim() === ''}>
                {t('offers.action.applyToAll')}
              </button>
            </div>
            <p className="offer-field-hint" id="offer-defaultDiscount-hint">
              {t('offers.hint.defaultDiscount')} {percent ? t('offers.hint.percent') : t('offers.hint.fixed')}
            </p>
          </div>
          {percent && (
            <div className="offer-field">
              <label htmlFor="offer-maxDiscountPerBooking">{t('offers.field.maxDiscountPerBooking')} <span className="offer-optional">({t('offers.field.optional')})</span></label>
              <input id="offer-maxDiscountPerBooking" type="number" inputMode="decimal" min={5} step="any" value={form.maxDiscountPerBooking}
                onChange={(e) => update('maxDiscountPerBooking', e.target.value)} {...errProps('maxDiscountPerBooking')} />
              <p className="offer-field-hint">{t('offers.hint.maxDiscountPerBooking')}</p>
              {err('maxDiscountPerBooking')}
            </div>
          )}
          <div className="offer-field">
            <label htmlFor="offer-maxBookings">{t('offers.field.maxBookings')} <span className="offer-optional">({t('offers.field.optional')})</span></label>
            <input id="offer-maxBookings" type="number" inputMode="numeric" min={Math.max(1, offer?.bookingsUsed ?? 1)} max={MAX_BOOKINGS_MAX} step={1}
              value={form.maxBookings} onChange={(e) => update('maxBookings', e.target.value)} {...errProps('maxBookings')} />
            <p className="offer-field-hint">{t('offers.hint.maxBookings')}</p>
            {err('maxBookings')}
          </div>
        </div>
      </fieldset>

      <fieldset className="offer-fieldset" disabled={readOnly}>
        <legend>{t('offers.field.services')}</legend>
        {services == null && !servicesError && <p className="offer-muted">{t('offers.services.loading')}</p>}
        {servicesError && <div className="error-message">{t('offers.services.loadFailed')}</div>}
        {services != null && options.length === 0 && <p className="offer-muted">{t('offers.services.empty')}</p>}
        {options.length > 0 && (
          <>
            <div className="offer-muted offer-selected-count">{t('offers.services.selected', { n: form.items.length })}</div>
            <ul className="offer-service-list" {...errProps('offeringIds')}>
              {options.map((o) => {
                const item = form.items.find((i) => i.offeringId === o.id);
                const checked = !!item;
                const list = o.price;
                const value = rowValues.get(o.id) ?? null;
                const after = list != null && value != null ? previewDiscountedPrice(list, form.discountType, value, maxPerNum) : null;
                const rowErrKey = itemField(o.id);
                const rowErrId = `offer-err-item-${o.id}`;
                return (
                  <li key={o.id} data-testid={`offer-service-${o.id}`}
                    className={`offer-service${checked ? ' offer-service--checked' : ''}${o.inactive ? ' offer-service--inactive' : ''}`}>
                    <label>
                      <input type="checkbox" checked={checked} onChange={() => toggleService(o.id)} />
                      <span className="offer-service__name">{o.name}</span>
                    </label>
                    <div className="offer-service__end">
                      {item && (
                        <span className="offer-service__value" dir="ltr">
                          <input
                            type="number"
                            inputMode="decimal"
                            min={percent ? PERCENT_MIN : FIXED_MIN}
                            max={percent ? PERCENT_MAX : (list != null ? Math.max(list - 1, FIXED_MIN) : undefined)}
                            step="any"
                            value={item.discountValue}
                            aria-label={t('offers.services.valueFor', { name: o.name })}
                            data-testid={`offer-item-value-${o.id}`}
                            onChange={(e) => setItemValue(o.id, e.target.value)}
                            {...(fieldErrors[rowErrKey] ? { 'aria-invalid': true as const, 'aria-describedby': rowErrId } : {})}
                          />
                          <span className="offer-service__unit" aria-hidden="true">{unit}</span>
                        </span>
                      )}
                      <span className="offer-service__price" dir="ltr" data-testid={`offer-item-price-${o.id}`}>
                        {list != null ? (
                          checked && after != null && after !== list
                            ? <><s>{formatAed(list)}</s> <b>{formatAed(after)}</b></>
                            : o.ranged ? <>{t('offers.services.from')} {formatAed(list)}</> : formatAed(list)
                        ) : '—'}
                      </span>
                    </div>
                    {fieldErrors[rowErrKey] && (
                      <div className="offer-field-error offer-service__error" id={rowErrId} role="alert" data-testid={`offer-item-error-${o.id}`}>
                        {fieldErrors[rowErrKey]}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {err('offeringIds')}
        {err('discountValue')}
        <p className="offer-field-hint">{t('offers.services.previewNote')}</p>
      </fieldset>

      {/* Outside the fieldsets so the tables stay expandable on a read-only (pending) offer. */}
      <OfferVariantPrices state={variantPreview} t={t} />
    </div>
  );

  const bannerStep = (
    <div className="offer-step-body offer-grid-2">
      <BannerUploader
        token={token ?? ''}
        label={t('offers.banner.en')}
        value={{ storageKey: form.bannerStorageKey, url: form.bannerUrl }}
        disabled={readOnly}
        error={fieldErrors.banner}
        testId="offer-banner-en"
        suggestions
        onChange={(v) => {
          setForm((f) => ({ ...f, bannerStorageKey: v.storageKey, bannerUrl: v.url }));
          setDirty(true);
          setFormError(null);
          setFieldErrors((e) => { const n = { ...e }; delete n.banner; return n; });
        }}
      />
      <BannerUploader
        token={token ?? ''}
        label={`${t('offers.banner.ar')} (${t('offers.field.optional')})`}
        hint={t('offers.banner.arHint')}
        value={{ storageKey: form.bannerArStorageKey, url: form.bannerArUrl }}
        disabled={readOnly}
        error={fieldErrors.bannerAr}
        testId="offer-banner-ar"
        onChange={(v) => {
          setForm((f) => ({ ...f, bannerArStorageKey: v.storageKey, bannerArUrl: v.url }));
          setDirty(true);
          setFormError(null);
          setFieldErrors((e) => { const n = { ...e }; delete n.bannerAr; return n; });
        }}
      />
    </div>
  );

  // Spec 073: one value, or the range across the services ("10–30%").
  const minValue = minItemValue(form);
  const maxValue = maxItemValue(form);
  const fmtValue = (v: number) => (percent ? `${v}%` : formatAed(v));
  const discountSummary = minValue == null || maxValue == null ? fmtValue(Number(form.defaultDiscount) || 0)
    : minValue === maxValue ? fmtValue(minValue)
      : percent ? `${minValue}–${maxValue}%` : `${formatAed(minValue)}–${maxValue}`;

  const previewStep = (
    <div className="offer-step-body offer-preview-layout">
      <div className="offer-preview-controls">
        <p className="offer-muted">{t('offers.preview.intro')}</p>
        <div className="offer-segment" role="group" aria-label={t('offers.preview.language')}>
          <button type="button" aria-pressed={previewLang === 'en'} className={previewLang === 'en' ? 'is-on' : ''} onClick={() => setPreviewLang('en')}>{t('offers.preview.en')}</button>
          <button type="button" aria-pressed={previewLang === 'ar'} className={previewLang === 'ar' ? 'is-on' : ''} onClick={() => setPreviewLang('ar')}>{t('offers.preview.ar')}</button>
        </div>
        <div className="offer-segment" role="group" aria-label={t('offers.preview.theme')}>
          <button type="button" aria-pressed={previewTheme === 'light'} className={previewTheme === 'light' ? 'is-on' : ''} onClick={() => setPreviewTheme('light')}>{t('offers.preview.light')}</button>
          <button type="button" aria-pressed={previewTheme === 'dark'} className={previewTheme === 'dark' ? 'is-on' : ''} onClick={() => setPreviewTheme('dark')}>{t('offers.preview.dark')}</button>
        </div>
        <dl className="offer-summary">
          <dt>{t('offers.col.window')}</dt>
          <dd dir="ltr">{formatDate(form.startsAt, lang)} – {formatDate(form.endsAt, lang)}</dd>
          <dt>{t('offers.field.discountType')}</dt>
          <dd dir="ltr" data-testid="offer-summary-discount">
            {discountSummary}
            {form.discountType === 'PERCENT' && maxPerNum != null ? ` (max ${formatAed(maxPerNum)})` : ''}
          </dd>
          <dt>{t('offers.field.maxBookings')}</dt>
          <dd>{form.maxBookings || t('offers.budget.unlimited')}</dd>
          <dt>{t('offers.col.services')}</dt>
          <dd>{form.items.length}</dd>
        </dl>
        {(fieldErrors.banner || fieldErrors.offeringIds) && (
          <div className="offer-field-error" role="alert">{fieldErrors.banner ?? fieldErrors.offeringIds}</div>
        )}
      </div>
      <OfferPhonePreview
        lang={previewLang}
        theme={previewTheme}
        garageName={garageName}
        titleEn={form.titleEn}
        titleAr={form.titleAr}
        subtitleEn={form.subtitleEn}
        subtitleAr={form.subtitleAr}
        discountType={form.discountType}
        discountValue={badgeValue}
        badgeUpTo={badgeUpTo}
        bannerUrl={form.bannerUrl}
        bannerArUrl={form.bannerArUrl}
        startsAt={form.startsAt}
        endsAt={form.endsAt}
        items={previewItems}
        maxBookings={form.maxBookings.trim() === '' ? null : Number(form.maxBookings)}
        bookingsUsed={offer?.bookingsUsed ?? 0}
        today={today}
      />
    </div>
  );

  const stepBodies = [detailsStep, servicesStep, bannerStep, previewStep];
  const submitLabel = isRunning ? t('offers.bar.submitChanges') : t('offers.bar.submit');
  const saveLabel = isRunning ? t('offers.bar.saveChanges') : t('offers.bar.saveDraft');

  return (
    <div className="offer-editor">
      <button type="button" className="offer-back" onClick={() => navigate('/promotions')}>
        <span aria-hidden="true">{lang === 'ar' ? '→' : '←'}</span> {t('offers.editor.back')}
      </button>
      <div className="offer-editor__head">
        <h2>{heading}</h2>
        {baseStatus && <span className={statusChipClass(baseStatus)} data-testid="offer-status">{t(`offers.status.${baseStatus}`)}</span>}
        {offer?.pendingRevision && <span className="offer-subchip">{t('offers.pendingRevision')}</span>}
      </div>
      {offer?.adminEditedAt && (
        <p className="offer-admin-edited" data-testid="offer-admin-edited">
          {t('offers.adminEdited', { date: formatInstantDate(offer.adminEditedAt, lang) })}
        </p>
      )}

      {notices}

      <ol className="offer-stepper" aria-label="Steps">
        {STEP_KEYS.map((key, i) => (
          <li key={key} className={`offer-stepper__item${i === step ? ' is-current' : ''}${i < step ? ' is-done' : ''}`}>
            <button type="button" onClick={() => goToStep(i)} aria-current={i === step ? 'step' : undefined} disabled={!!busy || (!offer && i > step + 1)}>
              <span className="offer-stepper__num">{i + 1}</span>
              <span className="offer-stepper__label">{t(key)}</span>
            </button>
          </li>
        ))}
      </ol>

      <section className="offer-card" aria-label={t(STEP_KEYS[step])}>
        {stepBodies[step]}
      </section>

      {formError && <div className="error-message offer-form-error" role="alert">{formError}</div>}

      <div className="offer-submit-bar">
        <div className="offer-submit-bar__start">
          {step > 0 && (
            <button type="button" className="offer-btn offer-btn--ghost" onClick={() => goToStep(step - 1)} disabled={!!busy}>
              {t('offers.bar.back')}
            </button>
          )}
        </div>
        <div className="offer-submit-bar__end">
          {canWithdraw && (
            <button type="button" className="offer-btn offer-btn--danger-ghost" onClick={() => setConfirmWithdraw(true)} disabled={!!busy}>
              {t('offers.bar.withdraw')}
            </button>
          )}
          {!readOnly && (
            <button type="button" className="offer-btn offer-btn--ghost" onClick={saveDraft} disabled={!!busy}>
              {busy === 'save' ? t('offers.bar.saving') : saveLabel}
            </button>
          )}
          {step < LAST_STEP && (
            <button type="button" className="offer-btn offer-btn--primary" onClick={() => goToStep(step + 1)} disabled={!!busy}>
              {busy === 'step' ? t('offers.bar.saving') : t('offers.bar.next')}
            </button>
          )}
          {step === LAST_STEP && canSubmit && (
            <button type="button" className="offer-btn offer-btn--primary" onClick={submit} disabled={!!busy}>
              {busy === 'submit' ? t('offers.bar.saving') : submitLabel}
            </button>
          )}
        </div>
      </div>

      {confirmWithdraw && (
        <ConfirmDialog
          title={t('offers.confirm.withdraw.title')}
          body={t('offers.confirm.withdraw.body')}
          confirmLabel={t('offers.action.withdraw')}
          cancelLabel={t('offers.confirm.cancel')}
          busy={busy === 'withdraw'}
          onConfirm={withdraw}
          onCancel={() => setConfirmWithdraw(false)}
        />
      )}
      {toast && <div className="partner-toast success" role="status">{toast}</div>}
    </div>
  );
}

export default OfferEditorPage;
